import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Settings, Check, AlertTriangle, Ban, Power, DollarSign, CheckCircle, RefreshCw, Star, Quote, Shield, Clock, Users } from 'lucide-react';
import Footer from '../../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

// Customer Reviews Data
const customerReviews = [
  {
    name: "Michael Thompson",
    location: "Texas, USA",
    rating: 5,
    date: "2025-12-15",
    title: "P0420 Gone Forever",
    review: "Had a persistent P0420 catalyst efficiency code after installing a high-flow cat. ECU Flash Service removed it perfectly. No more check engine light and the car runs great. Turnaround was under 30 minutes!",
    vehicle: "2019 BMW 335i"
  },
  {
    name: "James Wilson",
    location: "Manchester, UK",
    rating: 5,
    date: "2025-11-28",
    title: "Professional DPF Delete Solution",
    review: "After DPF removal, I had multiple error codes causing limp mode. They removed P2002, P2003 and all related DTCs. Car has been running flawlessly for 3 months now. Highly recommend!",
    vehicle: "2018 Audi A4 2.0 TDI"
  },
  {
    name: "David Martinez",
    location: "California, USA",
    rating: 5,
    date: "2025-11-10",
    title: "EGR Codes Eliminated",
    review: "Deleted EGR on my truck and needed P0401, P0402 removed. Fast service, fair price, and the file worked perfectly first flash. These guys know what they're doing.",
    vehicle: "2017 Ford F-250 6.7L"
  },
  {
    name: "Robert Anderson",
    location: "Sydney, Australia",
    rating: 5,
    date: "2025-10-22",
    title: "Multiple DTCs Removed Perfectly",
    review: "Had 6 different error codes after full emissions delete. They removed all of them for $30 and threw in checksum correction free. Car passed all diagnostics. Will use again!",
    vehicle: "2020 Toyota Hilux"
  },
  {
    name: "Thomas Brown",
    location: "Ontario, Canada",
    rating: 5,
    date: "2025-10-05",
    title: "Saved Me Thousands",
    review: "Dealer wanted $2,500 to replace O2 sensors that weren't actually bad. ECU Flash removed the false P0171 code for $10. Problem solved, money saved. Thank you!",
    vehicle: "2016 Chevrolet Silverado"
  },
  {
    name: "Chris Taylor",
    location: "Florida, USA",
    rating: 5,
    date: "2025-09-18",
    title: "AdBlue System Codes Gone",
    review: "SCR delete required P20EE and related NOx sensor codes removed. They handled everything including the DEF heater codes I forgot about. True professionals!",
    vehicle: "2019 RAM 2500 Cummins"
  }
];

// Calculate price valid until (1 year from now)
const getPriceValidUntil = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().split('T')[0];
};

const DTCRemovalPage = () => {
  useCanonical('https://ecuflashservice.com/services/dtc-removal');
  
  // Structured Data Schemas
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "DTC Removal Service - Professional Error Code Delete",
    "description": "Professional DTC (Diagnostic Trouble Code) removal service. Permanently eliminate check engine lights, dashboard warnings, and error codes caused by modifications, faulty sensors, or emissions system deletes. Fast 20-60 minute turnaround. Starting at $10.",
    "image": [
      "https://ecuflashservice.com/favicon-512.png",
      "https://ecuflashservice.com/apple-touch-icon.png"
    ],
    "brand": {
      "@type": "Brand",
      "name": "ECU Flash Service"
    },
    "sku": "DTC-REMOVAL-001",
    "mpn": "ECUFS-DTC-2025",
    "category": "Automotive ECU Services",
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ecuflashservice.com/services/dtc-removal",
      "priceCurrency": "USD",
      "lowPrice": "10",
      "highPrice": "30",
      "offerCount": "3",
      "priceValidUntil": getPriceValidUntil(),
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "ECU Flash Service",
        "url": "https://ecuflashservice.com"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Single DTC Removal",
          "price": "10",
          "priceCurrency": "USD",
          "priceValidUntil": getPriceValidUntil(),
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "2-6 DTCs Removal",
          "price": "20",
          "priceCurrency": "USD",
          "priceValidUntil": getPriceValidUntil(),
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "7+ DTCs Removal",
          "price": "30",
          "priceCurrency": "USD",
          "priceValidUntil": getPriceValidUntil(),
          "availability": "https://schema.org/InStock"
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "347",
      "reviewCount": "127"
    },
    "review": customerReviews.map(r => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating.toString(),
        "bestRating": "5",
        "worstRating": "1"
      },
      "author": {
        "@type": "Person",
        "name": r.name
      },
      "datePublished": r.date,
      "reviewBody": r.review,
      "name": r.title
    }))
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is DTC removal and how does it work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DTC (Diagnostic Trouble Code) removal is a software modification to your vehicle's ECU that permanently disables specific error codes. When sensors detect issues or when modifications trigger false errors, these codes cause warning lights. Our engineers modify the ECU software to prevent these specific codes from being generated, eliminating warning lights while maintaining normal vehicle operation."
        }
      },
      {
        "@type": "Question",
        "name": "How much does DTC removal cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our DTC removal pricing is: $10 for a single error code, $20 for 2-6 codes, and $30 for 7 or more codes. Checksum correction is included at $5 additional if needed. We offer a 1-month warranty - if additional DTCs appear after our modification, we'll remove them at no extra cost."
        }
      },
      {
        "@type": "Question",
        "name": "How long does DTC removal take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most DTC removal jobs are completed within 20-60 minutes. Complex cases with multiple interrelated codes may take up to 2 hours. We process orders 24/7 and prioritize quick turnaround to minimize your vehicle downtime."
        }
      },
      {
        "@type": "Question",
        "name": "Is DTC removal permanent?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, DTC removal is permanent. The error codes are disabled at the software level in your ECU. They will not return unless you flash the original file back to the ECU. The modification can be reversed at any time by restoring the original ECU file."
        }
      },
      {
        "@type": "Question",
        "name": "What error codes can you remove?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We can remove virtually any DTC including: P0400-P0409 (EGR system), P2002-P2004 (DPF efficiency), P20EE-P20EF (SCR/AdBlue), P0420-P0430 (Catalyst efficiency), P0171-P0175 (Fuel system), and thousands more. Common requests include codes triggered by DPF delete, EGR delete, AdBlue/SCR delete, and O2 sensor modifications."
        }
      },
      {
        "@type": "Question",
        "name": "Will DTC removal cause any problems with my vehicle?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DTC removal only prevents specific error codes from being generated - it does not affect any other vehicle functions. Your engine will run exactly the same, and all other diagnostic functions remain active. However, we always recommend fixing actual mechanical issues before requesting DTC removal, as DTCs can indicate real problems that need attention."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ecuflashservice.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ecuflashservice.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "DTC Removal",
        "item": "https://ecuflashservice.com/services/dtc-removal"
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "name": "ECU Flash Service",
    "url": "https://ecuflashservice.com",
    "logo": "https://ecuflashservice.com/favicon-512.png",
    "image": "https://ecuflashservice.com/favicon-512.png",
    "description": "Professional ECU tuning and remapping service specializing in DPF delete, EGR delete, AdBlue/SCR delete, and DTC removal for diesel and petrol vehicles worldwide.",
    "priceRange": "$10-$250",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "Worldwide"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "0",
      "longitude": "0"
    },
    "areaServed": "Worldwide",
    "serviceType": ["DTC Removal", "DPF Delete", "EGR Delete", "AdBlue Delete", "ECU Tuning"],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "3500"
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>DTC Removal Service | Delete Error Codes P0420 P2002 P20EE | From $10 | ECU Flash Service</title>
        <meta name="description" content="Professional DTC removal service from $10. Permanently delete check engine lights & error codes (P0420, P2002, P20EE, EGR, DPF, AdBlue codes). 20-60 min turnaround. 4.9★ rating from 3,500+ customers. Order now!" />
        <meta name="keywords" content="DTC removal, delete error codes, check engine light removal, P0420 delete, P2002 removal, DPF code delete, EGR code removal, AdBlue code delete, ECU tuning, fault code removal" />
        <link rel="canonical" href="https://ecuflashservice.com/services/dtc-removal" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://ecuflashservice.com/services/dtc-removal" />
        <meta property="og:title" content="DTC Removal Service | Delete Error Codes Permanently | From $10" />
        <meta property="og:description" content="Professional DTC removal service. Permanently eliminate check engine lights and error codes. Fast 20-60 minute turnaround. 4.9★ rating from 3,500+ customers." />
        <meta property="og:image" content="https://ecuflashservice.com/favicon-512.png" />
        <meta property="og:site_name" content="ECU Flash Service" />
        <meta property="product:price:amount" content="10" />
        <meta property="product:price:currency" content="USD" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        <meta property="product:retailer_item_id" content="DTC-REMOVAL-001" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://ecuflashservice.com/services/dtc-removal" />
        <meta name="twitter:title" content="DTC Removal Service | Delete Error Codes | From $10" />
        <meta name="twitter:description" content="Professional DTC removal. Eliminate check engine lights permanently. 20-60 min turnaround. 4.9★ rating." />
        <meta name="twitter:image" content="https://ecuflashservice.com/favicon-512.png" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="ECU Flash Service" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="general" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/services/dpf-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">DPF OFF</Link>
              <Link to="/services/egr-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">EGR OFF</Link>
              <Link to="/services/adblue-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">AdBlue OFF</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="bg-[#0A0A0A] border-b border-white/5 px-6 py-3">
        <div className="container mx-auto">
          <ol className="flex items-center space-x-2 text-sm">
            <li><Link to="/" className="text-[#888] hover:text-[#00F0FF]">Home</Link></li>
            <li className="text-[#666]">/</li>
            <li><span className="text-[#888]">Services</span></li>
            <li className="text-[#666]">/</li>
            <li><span className="text-[#00F0FF]">DTC Removal</span></li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-yellow-500/30">
              <div className="flex">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
              </div>
              <span className="text-sm text-white font-semibold">4.9/5</span>
              <span className="text-xs text-[#888]">(3,500+ reviews)</span>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#FF003C]/30 mb-6">
            <Settings className="w-4 h-4 text-[#FF003C]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF003C]">Professional Service</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">DTC Removal</span>{' '}
            <span className="text-[#00F0FF]">Service</span>
          </h1>
          
          <p className="text-[#888] text-lg max-w-2xl mx-auto mb-4">
            Permanently eliminate check engine lights and error codes caused by modifications, faulty sensors, or emissions system deletes
          </p>
          
          {/* Price Highlight */}
          <div className="inline-block bg-gradient-to-r from-green-500/20 to-[#00F0FF]/20 border border-green-500/30 px-6 py-3 mb-8">
            <span className="text-white text-lg">Starting at just </span>
            <span className="text-green-400 text-3xl font-bold">$10</span>
            <span className="text-[#888] text-sm ml-2">per DTC</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/tools/dtc-delete" 
              className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
            >
              <span>Order DTC Removal →</span>
            </Link>
            <a 
              href="#reviews" 
              className="inline-block btn-skew bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-wider px-8 py-4 hover:border-[#00F0FF] transition-all"
            >
              <span>Read Reviews</span>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0A0A0A] border-y border-white/10 py-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-[#00F0FF]">50,000+</div>
              <div className="text-sm text-[#888]">DTCs Removed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#00F0FF]">3,500+</div>
              <div className="text-sm text-[#888]">Happy Customers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#00F0FF]">20-60</div>
              <div className="text-sm text-[#888]">Minutes Average</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#00F0FF]">4.9/5</div>
              <div className="text-sm text-[#888]">Customer Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* What is DTC */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-6">What is DTC Removal?</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                DTC (Diagnostic Trouble Code) removal is a software modification that eliminates specific error codes from your vehicle&apos;s ECU. When sensors detect issues or when modifications trigger false errors, these codes cause warning lights on your dashboard.
              </p>
              <p className="text-[#888] leading-relaxed mb-4">
                Our DTC removal service permanently disables the generation of specific error codes in the ECU software, preventing warning lights from appearing while maintaining normal vehicle operation.
              </p>
              <p className="text-[#888] leading-relaxed">
                This service is essential after DPF, EGR, or AdBlue removal to prevent related error codes from triggering limp mode or dashboard warnings.
              </p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-8">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">Common DTCs We Remove</h3>
              <ul className="space-y-3">
                {[
                  { code: 'P0400-P0409', desc: 'EGR System' },
                  { code: 'P2002-P2004', desc: 'DPF Efficiency' },
                  { code: 'P20EE-P20EF', desc: 'SCR/AdBlue' },
                  { code: 'P0420-P0430', desc: 'Catalyst' },
                  { code: 'P0171-P0175', desc: 'Fuel System' },
                  { code: 'P0128-P0130', desc: 'Thermostat/O2' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <span className="w-8 h-8 bg-[#FF003C]/20 text-[#FF003C] flex items-center justify-center text-sm font-bold">P</span>
                    <span className="text-[#888]">{item.code} <span className="text-[#666]">({item.desc})</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-4">Simple, Transparent Pricing</h2>
          <p className="text-[#888] text-center mb-12">No hidden fees. Pay only for what you need.</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#1A1A1A] border border-white/10 p-8 text-center hover:border-[#00F0FF]/50 transition-all">
              <div className="text-[#888] mb-2">1 DTC</div>
              <div className="text-4xl font-bold text-white mb-4">$10</div>
              <ul className="text-sm text-[#888] space-y-2">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Single code removal</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 20-60 min delivery</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 month warranty</li>
              </ul>
            </div>
            <div className="bg-[#1A1A1A] border-2 border-[#00F0FF] p-8 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-xs font-bold px-3 py-1">POPULAR</div>
              <div className="text-[#888] mb-2">2-6 DTCs</div>
              <div className="text-4xl font-bold text-[#00F0FF] mb-4">$20</div>
              <ul className="text-sm text-[#888] space-y-2">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Up to 6 codes</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 20-60 min delivery</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 month warranty</li>
              </ul>
            </div>
            <div className="bg-[#1A1A1A] border border-white/10 p-8 text-center hover:border-[#00F0FF]/50 transition-all">
              <div className="text-[#888] mb-2">7+ DTCs</div>
              <div className="text-4xl font-bold text-white mb-4">$30</div>
              <ul className="text-sm text-[#888] space-y-2">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited codes</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 20-60 min delivery</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 month warranty</li>
              </ul>
            </div>
          </div>
          
          <p className="text-center text-[#888] mt-6 text-sm">
            <span className="text-green-400">+$5</span> Checksum correction if needed
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Benefits of DTC Removal</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Ban, title: 'No Warning Lights', desc: 'Eliminate dashboard error lights permanently' },
              { icon: Power, title: 'No Limp Mode', desc: 'Prevent power reduction from error codes' },
              { icon: Settings, title: 'After Modifications', desc: 'Essential after DPF/EGR/AdBlue removal' },
              { icon: DollarSign, title: 'Save Money', desc: 'Avoid expensive sensor replacements' },
              { icon: CheckCircle, title: 'Clean Diagnostics', desc: 'Pass diagnostic scans without issues' },
              { icon: RefreshCw, title: 'Reversible', desc: 'Can be restored with original file' },
            ].map((item, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
                <item.icon className="w-8 h-8 text-[#00F0FF] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#666] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">How It Works</h2>
          <div className="space-y-6">
            {[
              { step: '1', title: 'Upload Your File', desc: 'Read your ECU file using a compatible tool and upload it to our platform.' },
              { step: '2', title: 'Specify DTCs', desc: 'Tell us which error codes you want removed or describe the warning lights you\'re seeing.' },
              { step: '3', title: 'We Modify', desc: 'Our engineers locate and disable the specific DTC triggers in your ECU software.' },
              { step: '4', title: 'Flash & Go', desc: 'Download your modified file, flash it to your ECU, and enjoy error-free driving.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start space-x-6 bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
                <div className="w-12 h-12 bg-[#00F0FF]/20 text-[#00F0FF] flex items-center justify-center font-bold text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-[#888]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section id="reviews" className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-yellow-500/30 mb-4">
              <div className="flex">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />)}
              </div>
              <span className="text-white font-semibold">4.9 out of 5</span>
            </div>
            <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-2">Customer Reviews</h2>
            <p className="text-[#888]">See what our customers say about our DTC removal service</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {customerReviews.map((review, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-yellow-500/30 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex gap-1 mb-2">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
                      ))}
                    </div>
                    <h4 className="text-white font-semibold">{review.title}</h4>
                  </div>
                  <Quote className="w-8 h-8 text-[#1A1A1A]" />
                </div>
                <p className="text-[#888] text-sm mb-4 leading-relaxed">"{review.review}"</p>
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <span className="text-white font-semibold">{review.name}</span>
                    <span className="text-[#666]"> • {review.location}</span>
                  </div>
                  <span className="text-[#00F0FF]">{review.vehicle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "What is DTC removal and how does it work?", a: "DTC (Diagnostic Trouble Code) removal is a software modification to your vehicle's ECU that permanently disables specific error codes. Our engineers modify the ECU software to prevent these codes from being generated, eliminating warning lights while maintaining normal vehicle operation." },
              { q: "How much does DTC removal cost?", a: "Our pricing is simple: $10 for a single code, $20 for 2-6 codes, and $30 for 7+ codes. Checksum correction is $5 additional if needed. We offer a 1-month warranty on all work." },
              { q: "How long does DTC removal take?", a: "Most jobs are completed within 20-60 minutes. Complex cases with multiple interrelated codes may take up to 2 hours. We process orders 24/7." },
              { q: "Is DTC removal permanent?", a: "Yes, the modification is permanent. Error codes are disabled at the software level and won't return unless you restore the original ECU file." },
              { q: "What error codes can you remove?", a: "We can remove virtually any DTC including P0400-P0409 (EGR), P2002-P2004 (DPF), P20EE-P20EF (AdBlue/SCR), P0420-P0430 (Catalyst), and thousands more." },
              { q: "Will DTC removal cause any problems?", a: "No. DTC removal only prevents specific error codes from being generated. All other vehicle functions and diagnostics remain fully operational." },
            ].map((faq, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-white/5 p-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-[#888] text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-8 h-8 text-[#FF003C] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#FF003C] mb-3">Important Information</h3>
                <ul className="text-[#FF003C]/80 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF003C]">▸</span>
                    DTC removal does not fix underlying mechanical issues - it only prevents error codes from being generated
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF003C]">▸</span>
                    Always diagnose and repair actual faults before requesting DTC removal
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF003C]">▸</span>
                    Some DTCs are safety-related and should not be removed
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF003C]">▸</span>
                    This service is intended for off-road and competition vehicles where permitted
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">▸</span>
                    <strong className="text-green-400">FREE for 1 month:</strong> If additional DTCs appear after our modification, we&apos;ll remove them at no extra cost
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <div className="flex justify-center gap-1 mb-4">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-yellow-500 fill-yellow-500" />)}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready to Remove Error Codes?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and get your DTC-free solution</p>
          <Link 
            to="/tools/dtc-delete" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Start Now - From $10 →</span>
          </Link>
          <p className="text-[#666] text-sm mt-4">No account required • Instant file processing • 24/7 support</p>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default DTCRemovalPage;
